/**
 * Comisión reconciliation — bridge the on-the-fly reportes to the ledger while
 * staying backward compatible.
 *
 * A fianza's comisión is authoritative in the ledger (orgs/{org}/comisiones)
 * once it has movements (booked at emisión / endoso, or by the backfill). Until
 * then — legacy data, pre-backfill, or a non-owner who can't read the ledger —
 * the reportes compute it on the fly exactly as before. This resolver returns
 * whichever applies per fianza, so a reporte keeps iterating fianzas (no
 * double-counting) and only swaps where the comisión number comes from.
 *
 * tarifa and primaMinima are ALWAYS computed on the fly: they are per-fianza
 * rate metrics unaffected by the money ledger, and primaMinima is not reliably
 * captured on movements.
 */
import {
  getPrimaAmount,
  calculateTarifa,
  calculateComision,
  getEmisorRfc,
  toTimestamp,
  partitionTarifas,
  resolveParticipacion,
  resolveSplit,
} from './calculations';

// fianzaId -> [movements]. Fianzas absent from the map fall back to on-the-fly.
export function indexMovementsByFianza(movements) {
  const byFianza = new Map();
  (movements || []).forEach((m) => {
    if (!m?.fianzaId) return;
    if (!byFianza.has(m.fianzaId)) byFianza.set(m.fianzaId, []);
    byFianza.get(m.fianzaId).push(m);
  });
  return byFianza;
}

/**
 * Resolve a fianza's comisión: ledger-authoritative when movements exist, else
 * on-the-fly. Returns the same fields the reportes already use, plus fromLedger.
 *
 * `opts` carries the convenio-split context (mirrors the backend):
 *   - contractData: the contract the fianza lives on. convenioId set means the
 *     fianza prices off the FIADO agent's % (locked on the fianza, else the
 *     convenio-scope tarifa estimate) and the org nets the beneficiario share.
 *   - orgConfig:   orgs/{org}/config/comisiones (participación defaults).
 *   - convenioData: the convenio doc (its participacion overrides orgConfig).
 * Without opts the behavior is the pre-split one (propia tables, gross).
 *
 * @returns {{prima:number, comisionMonto:number|null, comisionPct:number|null,
 *            tarifa:number|null, primaMinima:boolean, fromLedger:boolean,
 *            splitRol:string|null, splitPct:number|null, pctEstimado:boolean}}
 */
export function resolveComision(fianza, movementsForFianza, tarifas, emisorRaw, subRamo, tipo, opts = {}) {
  const { contractData = null, orgConfig = null, convenioData = null } = opts;
  const montoAfianzado = Number(fianza?.montoAfianzado) || 0;
  const ontheflyPrima = getPrimaAmount(fianza);
  const vigencia = {
    inicio: toTimestamp(fianza?.inicioVigenciaFianza),
    fin: toTimestamp(fianza?.finVigenciaFianza),
  };
  const tarifa = calculateTarifa(ontheflyPrima, montoAfianzado, { truncate: true, vigencia });

  // A convenio fianza's base % is another agent's deal: the convenio-scope
  // tables — else the org's propia tables — act as the default ESTIMATE
  // (mirrors comision_service.resolve_pct_with_default).
  const { propias, convenio: tablasConvenio } = partitionTarifas(tarifas);
  const esConvenio = Boolean(contractData?.convenioId);
  const tablas = esConvenio ? tablasConvenio : propias;
  const emisorRfc = getEmisorRfc(fianza);
  let onthefly = calculateComision(ontheflyPrima, tarifa, tablas, emisorRaw, subRamo, tipo, emisorRfc);
  if (esConvenio && onthefly.comisionPct == null) {
    const fallback = calculateComision(ontheflyPrima, tarifa, propias, emisorRaw, subRamo, tipo, emisorRfc);
    if (fallback.comisionPct != null) onthefly = fallback;
  }

  const participacion = resolveParticipacion(emisorRaw, orgConfig, convenioData);
  const { rol: splitRol, factor, splitPct } = resolveSplit(contractData, fianza, participacion);

  if (movementsForFianza && movementsForFianza.length > 0) {
    // Σ over movements: positive (emision/adicional/sustituye) + negative (cargo)
    // net to the fianza's current valid prima and net comisión. Movements are
    // already NET of the split — no factor here.
    //
    // The Σ-invariant only holds when the ledger starts at the fianza's own
    // emisión movement (booked at emisión, or for the nueva fianza of a
    // sustitución/renovación). A pre-backfill fianza whose first movement is an
    // endoso delta would report the delta alone as its prima — and the tarifa
    // computed from it — so an incomplete ledger reads the fianza's prima
    // vigente instead.
    const ledgerCompleto = movementsForFianza.some((m) => m.tipo === 'emision');
    const prima = ledgerCompleto
      ? movementsForFianza.reduce((s, m) => s + (Number(m.prima) || 0), 0)
      : ontheflyPrima;
    const comisionMonto = movementsForFianza.reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
    const locked = movementsForFianza.find((m) => m.comisionPct != null)?.comisionPct;
    return {
      prima,
      comisionMonto,
      comisionPct: locked != null ? locked : onthefly.comisionPct,
      tarifa,
      primaMinima: onthefly.primaMinima,
      fromLedger: true,
      splitRol,
      splitPct,
      pctEstimado: movementsForFianza.some((m) => m.pctEstimado),
    };
  }

  // On-the-fly (no movements). The locked % on the fianza wins; else the
  // derived one — which is an ESTIMATE when it came from a default table
  // (convenio-scope / propia for convenio fianzas, or the org's Tabla
  // general for any fianza without its own table). The org's share is the
  // net through the split factor.
  const lockedPct = fianza?.comisionPct != null ? Number(fianza.comisionPct) : null;
  const pct = lockedPct != null ? lockedPct : onthefly.comisionPct;
  const bruta = pct != null ? (ontheflyPrima * pct) / 100 : null;
  return {
    prima: ontheflyPrima,
    comisionMonto: bruta == null ? null : bruta * factor,
    comisionPct: pct,
    tarifa,
    primaMinima: onthefly.primaMinima,
    fromLedger: false,
    splitRol,
    splitPct,
    pctEstimado: Boolean(
      lockedPct == null &&
      onthefly.comisionPct != null &&
      (esConvenio || onthefly.pctEstimado)
    ),
  };
}
